import { useEffect, useMemo, useRef } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { TourKeys } from '@cast/constants';
import { Node, NodeResponse } from '@cast/types';
import {
  createScalarStore,
  makeIsEnabled,
  useSynchronizedRefetch,
} from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useTourStepId } from 'core/tour-v2/hooks/useTourStepId';
import { TourSteps } from 'features/demo/types';
import { enhanceNode } from 'utils/api-utils';

const EXTERNAL_CLUSTER_NODES_QUERY_KEY = 'external-cluster-nodes';

/** Default page size — passing pageLimit opts out of API middleware full-list aggregation. */
const NODES_PAGE_LIMIT = 50;

export const getNodesQueryKey = (
  params: {
    clusterId?: string;
    tourStepId?: string | null;
    pageLimit?: number;
  } = {}
) => {
  if (params.tourStepId != null) {
    return [
      EXTERNAL_CLUSTER_NODES_QUERY_KEY,
      params.clusterId,
      params.pageLimit,
      params.tourStepId,
    ] as const;
  }
  if (params.clusterId != null) {
    return [
      EXTERNAL_CLUSTER_NODES_QUERY_KEY,
      params.clusterId,
      params.pageLimit,
    ] as const;
  }
  return [EXTERNAL_CLUSTER_NODES_QUERY_KEY] as const;
};

const syncGroup = `${TourKeys.OpinionatedPlatform}-nodes-deletion`;
const useNodeDeletionIndex = createScalarStore(0);

type UseNodesQueryArgs = {
  clusterId?: string;
  refetchInterval?: number | false;
  enabled?: boolean;
  pageLimit?: number;
  /**
   * Keep requesting pages until exhausted (Node Details master count, etc.).
   * Inventory List uses scroll/`fetchNextPage` instead.
   */
  fetchAllPages?: boolean;
};

type NodesPage = {
  items: Node[];
  nextCursor?: string;
};

const getNodesInfiniteQueryOptions = ({
  clusterId,
  tourStepId,
  demoFetchIndexRef,
  enabled,
  refetchInterval,
  syncGroup,
  pageLimit,
}: {
  clusterId?: string;
  tourStepId?: string | null;
  demoFetchIndexRef: { current: number };
  enabled?: boolean;
  refetchInterval?: number | false;
  syncGroup: string;
  pageLimit: number;
}) =>
  infiniteQueryOptions({
    queryKey: getNodesQueryKey({
      clusterId: clusterId!,
      tourStepId,
      pageLimit,
    }),
    queryFn: async ({ pageParam }): Promise<NodesPage> => {
      const { data } = await apiClient.clusters.getNodes(
        {
          clusterId: clusterId!,
          pageLimit: String(pageLimit),
          pageCursor: pageParam || undefined,
        },
        {
          middlewareArgs: {
            requestMeta: { demoFetchIndex: demoFetchIndexRef.current },
          },
        }
      );
      return {
        items:
          data.items?.map((node) => enhanceNode(node as NodeResponse)) || [],
        nextCursor: data.nextCursor,
      };
    },
    initialPageParam: '',
    getNextPageParam: (lastPage: NodesPage) =>
      lastPage.nextCursor?.length ? lastPage.nextCursor : undefined,
    enabled: makeIsEnabled(!!clusterId, enabled),
    refetchInterval,
    meta: {
      syncGroup,
    },
  });

export const useNodesQuery = ({
  clusterId,
  refetchInterval = false,
  enabled = true,
  pageLimit = NODES_PAGE_LIMIT,
  fetchAllPages = true,
}: UseNodesQueryArgs = {}) => {
  const tourStepId = useTourStepId(TourKeys.OpinionatedPlatform);

  const demoFetchIndex = useNodeDeletionIndex().value;

  // TODO (Justas): Remove during `https://castai.atlassian.net/browse/UI-5682`
  useEffect(() => {
    useNodeDeletionIndex.setValue(0);
  }, [tourStepId]);
  // Prevent stale closure
  const demoFetchIndexRef = useRef(demoFetchIndex);
  demoFetchIndexRef.current = demoFetchIndex;

  // Synchronized refetch: transition after 2 seconds
  useSynchronizedRefetch({
    syncGroup,
    intervalMs: 2000,
    enabled: tourStepId === TourSteps.GoToRebalancer && demoFetchIndex < 3,
    onRefetch: () => {
      useNodeDeletionIndex.setValue(demoFetchIndex + 1);
    },
  });

  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery(
    getNodesInfiniteQueryOptions({
      clusterId,
      tourStepId,
      demoFetchIndexRef,
      enabled,
      refetchInterval,
      syncGroup,
      pageLimit,
    })
  );

  useEffect(() => {
    if (!fetchAllPages || !hasNextPage || isFetchingNextPage) {
      return;
    }
    void fetchNextPage();
  }, [fetchAllPages, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const nodes = useMemo(
    () => flatMap(data?.pages, (page) => page.items),
    [data?.pages]
  );

  return {
    nodes,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    fetchNextPage,
    error,
    refetch,
  };
};
